import React, { useState, useEffect } from 'react';
import {
    Mail, Lock, Loader2, AlertCircle,
    CheckCircle2, Info, ArrowLeft, ShieldCheck
} from 'lucide-react';
import { supabase, extractErrorMessage } from '../services/supabaseService';
import { UserRole } from '../types';
import { CLIENT_PORTAL_LOGO_URL } from '../constants';

interface LoginProps {
    onDevLogin?: (role: UserRole) => void;
    isClientPortal?: boolean;
}

export const Login: React.FC<LoginProps> = ({ onDevLogin: _onDevLogin, isClientPortal }) => {
    const [view, setView] = useState<'login' | 'update_password' | 'otp_request' | 'otp_verify'>(isClientPortal ? 'otp_request' : 'login');
    const [processingAction, setProcessingAction] = useState<'email' | 'azure' | 'reset' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const [otpPin, setOtpPin] = useState('');

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        company: ''
    });

    useEffect(() => {
        const hash = window.location.hash;
        if (hash && hash.includes('type=recovery')) {
            setView('update_password');
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
            if (event === 'PASSWORD_RECOVERY') {
                setView('update_password');
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Admin "Test inloggen" opens clientportaal with ?portalEmail= — pre-fill OTP e-mailstap
    useEffect(() => {
        if (!isClientPortal) return;
        try {
            const q = new URLSearchParams(window.location.search).get('portalEmail');
            if (!q) return;
            const decoded = decodeURIComponent(q).trim();
            if (decoded.includes('@')) {
                setFormData((prev) => ({ ...prev, email: decoded }));
            }
        } catch {
            /* ignore */
        }
    }, [isClientPortal]);

    const checkSocialProviderOnly = async (emailToCheck: string) => {
        try {
            const { data: provs, error } = await supabase.rpc('get_user_auth_providers', { check_email: emailToCheck });
            if (!error && provs && Array.isArray(provs) && provs.length > 0) {
                if (provs.includes('azure') && !provs.includes('email')) {
                    throw new Error('Dit account is gekoppeld aan Microsoft. Gebruik «Inloggen met Microsoft» op de inlogpagina van je recruiter.');
                }
            }
        } catch (e: any) {
            if (e.message && e.message.includes('Microsoft')) {
                throw e;
            }
            console.warn("Could not verify auth providers (possibly not deployed yet). Proceeding with normal login.");
        }
    };

    const handleOtpRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setProcessingAction('email');
        setError(null);
        try {
            if (!isClientPortal) {
                await checkSocialProviderOnly(formData.email);
            }

            if (isClientPortal) {
                // Pre-flight check before hitting Auth API
                const { data: accessInfo, error: accessErr } = await supabase.rpc('check_portal_access', { user_email: formData.email });
                if (accessErr) {
                   console.warn("Could not verify portal access:", accessErr);
                   // Optionally fail open if RPC doesn't exist yet, or fail closed.
                   // Since we provide the RPC, we can fail closed if it's there.
                } else if (accessInfo && typeof accessInfo === 'object') {
                   if (!accessInfo.allowed) {
                      if (accessInfo.reason === 'orphaned_profile') {
                          throw new Error('Je account zit klem in ons systeem door een eerdere verwijdering. Neem contact op met support om je profiel te resetten.');
                      } else {
                          throw new Error('Je hebt geen toegang tot het klanten portaal. Vraag toegang aan via onze support.');
                      }
                   }
                }
            }

            const { error } = await supabase.auth.signInWithOtp({
                email: formData.email,
                options: { shouldCreateUser: true }
            });
            if (error) {
                if (error.message.includes('rate_limit')) throw new Error('Te veel pogingen. Wacht even voordat je het opnieuw probeert.');
                throw error;
            }
            setView('otp_verify');
        } catch (err: any) {
            console.error("OTP Request Error:", err);
            setError(extractErrorMessage(err));
        } finally {
            setProcessingAction(null);
        }
    };

    const handleOtpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setProcessingAction('email');
        setError(null);
        try {
            const { data, error } = await supabase.auth.verifyOtp({
                email: formData.email,
                token: otpPin.trim(),
                type: 'email'
            });
            if (error) {
                if (error.message.includes('Token has expired or is invalid')) throw new Error('Ongeldige of verlopen PIN code.');
                throw error;
            }
            if (data.session) {
                setLoginSuccess(true);
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            }
        } catch (err: any) {
            console.error("OTP Verify Error:", err);
            setError(extractErrorMessage(err));
        } finally {
            setProcessingAction(null);
        }
    };



    const handleMicrosoftLogin = async () => {
        setProcessingAction('azure');
        setError(null);
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'azure',
                options: {
                    redirectTo: `${window.location.origin}/`,
                    skipBrowserRedirect: true,
                    scopes: 'Mail.Send MailboxSettings.Read Calendars.ReadWrite Files.ReadWrite User.Read email profile',
                    queryParams: { prompt: 'select_account' },
                } as any,
            });

            if (error) throw error;

            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error('Geen redirect URL ontvangen van de authenticatieserver.');
            }
        } catch (err: any) {
            console.error('[SSO] Microsoft login error:', err);
            setError(extractErrorMessage(err));
            setProcessingAction(null);
        }
    };

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            setError("Wachtwoorden komen niet overeen.");
            return;
        }
        setProcessingAction('reset');
        setError(null);

        try {
            const { error } = await supabase.auth.updateUser({ password: formData.password });
            if (error) throw error;
            alert("Wachtwoord succesvol bijgewerkt!");
            setView('login');
            setFormData({ ...formData, password: '', confirmPassword: '' });
            window.history.replaceState(null, '', window.location.pathname);
        } catch (err: any) {
            setError(extractErrorMessage(err));
        } finally {
            setProcessingAction(null);
        }
    };

    return (
        <div className="flex bg-[#F8FAFC] min-h-screen w-full font-sans">
            {/* Left Side - Visual */}
            <div className="hidden lg:flex lg:w-[45%] relative bg-[#0F172A] m-4 rounded-[2rem] overflow-hidden shadow-2xl">
                <img
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1770&q=80"
                    className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-overlay"
                    alt="Modern Office"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/50 to-transparent" />

                <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white h-full">
                    <div className="flex items-center gap-3">
                        <img
                            src={CLIENT_PORTAL_LOGO_URL}
                            alt="Werftools"
                            className="h-10 w-auto max-w-[240px] object-contain object-left"
                        />
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-5xl font-black leading-[1.1] tracking-tight">
                            Slimmer recruitment begint{' '}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#e51b23] to-orange-500">
                                hier
                            </span>
                        </h2>
                        <p className="text-slate-400 font-medium text-lg max-w-md leading-relaxed">
                            De perfecte combinatie van AI, automatisering en menselijke expertise.
                            <br />
                            <br />
                            Ontworpen voor organisaties die recruitment efficiënter én persoonlijker willen maken.
                        </p>
                    </div>

                </div>
            </div>

            {/* Right Side - Forms */}
            <div className={`flex-1 flex flex-col items-center justify-center p-6 lg:p-12 relative ${!isClientPortal ? 'before:absolute before:inset-0 before:bg-gradient-to-br before:from-[#F8FAFC] before:to-white lg:before:hidden' : ''}`}>
                


                <div className={`w-full max-w-md animate-fadeIn relative z-10 ${
                    isClientPortal 
                        ? 'bg-transparent p-6 lg:p-0 space-y-8 lg:border-none' 
                        : 'bg-white p-8 lg:bg-transparent lg:p-0 rounded-3xl lg:rounded-none shadow-2xl lg:shadow-none space-y-8 border border-slate-100 lg:border-none'
                }`}>
                    {isClientPortal && (
                        <button onClick={() => { window.location.href = 'https://werftools.nl'; }} className="w-10 h-10 bg-white border border-slate-200 rounded-[14px] flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors shadow-sm mb-4">
                            <ArrowLeft size={18} />
                        </button>
                    )}
                    
                    <div className="text-left flex flex-col items-start w-full">
                        <div className={`flex flex-col items-start mb-6 w-full relative ${!isClientPortal ? 'lg:hidden' : ''}`}>
                            <img
                                src={CLIENT_PORTAL_LOGO_URL}
                                alt="Werftools"
                                className="h-12 w-auto max-w-[240px] object-contain mb-3"
                            />
                            {isClientPortal && (
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] bg-red-50 px-3 py-1.5 rounded-lg border border-red-100 shadow-sm">
                                    Clientportal
                                </span>
                            )}
                        </div>
                        <h2 className={`font-black text-slate-900 tracking-tight leading-[1.1] ${isClientPortal ? 'text-[32px]' : 'text-3xl'}`}>
                            {isClientPortal
                                ? (view === 'otp_verify' ? 'Voer PIN in' : 'Welkom terug! Fijn om je weer te zien.')
                                : (view === 'login' ? 'Inloggen met Microsoft' : 'Nieuw wachtwoord')}
                        </h2>
                        {!isClientPortal && (
                        <p className="text-slate-500 text-sm font-medium mt-2">
                            {view === 'login'
                                ? 'Gebruik je werk-Microsoft-account (Microsoft 365). E-mail en wachtwoord zijn uitgeschakeld.'
                                : 'Je sessie is geverifieerd. Stel hieronder een nieuw wachtwoord in (alleen na een herstel-link).'}
                        </p>
                        )}
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3 text-red-600 text-xs font-bold animate-slideDown">
                            <AlertCircle size={18} className="shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}

                    {successMsg && (
                        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-emerald-600 text-xs font-bold animate-slideDown">
                            <CheckCircle2 size={18} />
                            <span>{successMsg}</span>
                        </div>
                    )}

                    {/* RECRUITER: MICROSOFT ONLY */}
                    {view === 'login' && !isClientPortal && (
                        <div className="space-y-6">
                            <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex gap-3 text-slate-600 text-xs font-medium">
                                <Info size={18} className="shrink-0 text-slate-400" />
                                <p>
                                    Mail via Outlook, agenda en bestanden werken alleen als je hier met Microsoft inlogt. Geen apart Werftools-wachtwoord meer.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => void handleMicrosoftLogin()}
                                disabled={processingAction !== null}
                                className="w-full flex items-center justify-center gap-3 p-4 bg-[#0F172A] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#e51b23] hover:shadow-lg hover:shadow-red-500/20 transition-all shadow-sm active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {processingAction === 'azure' ? (
                                    <Loader2 className="animate-spin" size={22} />
                                ) : (
                                    <svg viewBox="0 0 24 24" className="w-6 h-6 shrink-0" aria-hidden="true">
                                        <path fill="#F25022" d="M1 1h10v10H1z" />
                                        <path fill="#7FBA00" d="M13 1h10v10H13z" />
                                        <path fill="#00A4EF" d="M1 13h10v10H1z" />
                                        <path fill="#FFB900" d="M13 13h10v10H13z" />
                                    </svg>
                                )}
                                Inloggen met Microsoft
                            </button>
                        </div>
                    )}

                    {/* FORGOT PASSWORD removed — recovery links from Supabase still open update_password. */}
                    {view === 'update_password' && (
                        <form onSubmit={handleUpdatePassword} className="space-y-6">
                            <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex gap-3 text-emerald-700 text-xs font-medium">
                                <ShieldCheck size={18} className="shrink-0" />
                                <p>Your session is verified. Please set a new, secure password below.</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">New Password</label>
                                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-slate-200 transition-all" required placeholder="••••••••" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Confirm</label>
                                <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-slate-200 transition-all" required placeholder="••••••••" />
                            </div>
                            <button type="submit" disabled={processingAction !== null} className="w-full bg-emerald-600 text-white p-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg active:scale-[0.98]">
                                {processingAction === 'reset' ? <Loader2 className="animate-spin" size={16} /> : 'Save Password'}
                            </button>
                        </form>
                    )}


                    {/* OTP REQUEST FORM (Email) */}
                    {view === 'otp_request' && (
                        <form onSubmit={handleOtpRequest} className="space-y-6">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Work Email</label>
                                <div className="relative">
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full p-4 pl-12 bg-white border border-slate-100 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-[#e51b23]/10 focus:border-[#e51b23] transition-all shadow-sm"
                                        placeholder="naam@klantbedrijf.nl"
                                        required
                                    />
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={processingAction !== null || loginSuccess}
                                className={`w-full p-4 rounded-[14px] font-bold text-sm transition-all flex items-center justify-center gap-2 active:scale-[0.98] mt-4 ${loginSuccess ? 'bg-emerald-500 text-white' : 'bg-[#1C212D] text-white hover:bg-[#e51b23] shadow-md hover:shadow-red-500/20'}`}
                            >
                                {processingAction === 'email' || loginSuccess ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : (
                                    'Code per e-mail versturen'
                                )}
                            </button>

                            <p className="text-center text-[11px] text-slate-500 font-medium leading-relaxed pt-2">
                                Je ontvangt een eenmalige code per e-mail. Geen wachtwoord nodig.
                            </p>
                        </form>
                    )}

                    {/* OTP VERIFY FORM (PIN) */}
                    {view === 'otp_verify' && (
                        <form onSubmit={handleOtpVerify} className="space-y-6">
                            <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex gap-3 text-emerald-700 text-xs font-medium mb-6">
                                <Mail size={18} className="shrink-0" />
                                <p>We hebben een login code (PIN) verzonden naar <strong>{formData.email}</strong>.</p>
                            </div>
                            
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">8-Cijferige PIN</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={otpPin}
                                        onChange={(e) => setOtpPin(e.target.value)}
                                        className="w-full p-4 pl-12 bg-white border border-slate-100 rounded-2xl text-sm font-bold tracking-[0.5em] text-center text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all shadow-sm"
                                        placeholder="12345678"
                                        maxLength={8}
                                        required
                                    />
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={processingAction !== null || loginSuccess || otpPin.length < 8}
                                className={`w-full p-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-3 active:scale-[0.98] mt-4 ${loginSuccess ? 'bg-emerald-500 text-white' : 'bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-500/20'}`}
                            >
                                {processingAction === 'email' || loginSuccess ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} />
                                        {loginSuccess ? 'Redirecting...' : 'Verifiëren...'}
                                    </>
                                ) : (
                                    <>
                                        Inloggen
                                        <CheckCircle2 size={16} />
                                    </>
                                )}
                            </button>
                            
                            <div className="text-center mt-6">
                                <button type="button" onClick={() => setView('otp_request')} className="text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-widest">
                                    Code niet ontvangen? Probeer opnieuw
                                </button>
                            </div>
                        </form>
                    )}


                </div>
            </div>
        </div>
    );
};
